// Kénitra → the hubs the parcel was scanned in → her city (owner, 2026-10-10).
// AMEEX never gives a live position, so the dot sits on the LAST scan — the
// line pulses while the parcel is moving, and stops when it is delivered or
// stuck. No map tiles, no library: real city centres on a plain projection.
import { CITY_COORDS, REFERENCE_CITIES } from './board';

const W = 320;
const BASE = { minLat: 29.2, maxLat: 36.1, minLon: -10.4, maxLon: -1.5 };
const LABEL: Record<string, string> = {
  tanger: 'Tanger', kenitra: 'Kénitra', casablanca: 'Casablanca', fes: 'Fès', oujda: 'Oujda', marrakech: 'Marrakech',
  agadir: 'Agadir', rabat: 'Rabat', meknes: 'Meknès', tetouan: 'Tétouan', laayoune: 'Laâyoune', dakhla: 'Dakhla',
};
const nice = (k: string) => LABEL[k] ?? k.replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * The country, simplified to ~45 points (lat, lon): the Mediterranean and
 * Atlantic coasts from Saïdia to Lagouira, then the land borders back north.
 * A backdrop for orientation only — the route on it is AMEEX's real scans.
 */
const MOROCCO: Array<[number, number]> = [
  [35.09, -2.21], [35.17, -2.93], [35.25, -3.93], [35.2, -4.67], [35.68, -5.33], [35.89, -5.32], [35.79, -5.81],
  [35.79, -5.92], [35.47, -6.03], [35.2, -6.16], [34.27, -6.66], [34.03, -6.84], [33.6, -7.62], [33.25, -8.51],
  [32.32, -9.26], [31.51, -9.77], [30.63, -9.89], [30.42, -9.62], [29.38, -10.17], [28.5, -11.33], [27.94, -12.93],
  [27.1, -13.4], [26.13, -14.48], [24.5, -15.8], [23.7, -15.95], [22.2, -16.7], [20.77, -17.05], [21.33, -16.95],
  [21.33, -13.0], [23.45, -12.0], [26.0, -8.67], [27.67, -8.67], [28.7, -8.67], [29.4, -7.6], [29.7, -6.0],
  [29.9, -5.2], [30.6, -4.2], [31.2, -3.65], [31.6, -3.3], [32.1, -2.9], [32.2, -1.4], [32.8, -1.2], [33.7, -1.7],
  [34.6, -1.75],
];

export function RouteMap({ route, destination, at: atCity, tone, moving, delivered }: {
  /** City keys in order: origin, hubs, her city. */
  route: string[];
  destination: string | null;
  /** Where AMEEX last scanned it. */
  at: string | null;
  tone: 'moving' | 'problem' | 'done';
  moving: boolean;
  delivered: boolean;
}) {
  const pts = route.map((k) => CITY_COORDS[k]).filter(Boolean);
  const b = { ...BASE };
  for (const [lat, lon] of pts) {
    b.minLat = Math.min(b.minLat, lat - 0.6); b.maxLat = Math.max(b.maxLat, lat + 0.6);
    b.minLon = Math.min(b.minLon, lon - 0.6); b.maxLon = Math.max(b.maxLon, lon + 0.6);
  }
  const midLat = ((b.minLat + b.maxLat) / 2) * (Math.PI / 180);
  const spanX = (b.maxLon - b.minLon) * Math.cos(midLat);
  const H = Math.round(W * ((b.maxLat - b.minLat) / spanX));
  const at = ([lat, lon]: [number, number]) => ({
    x: ((lon - b.minLon) * Math.cos(midLat) / spanX) * W,
    y: ((b.maxLat - lat) / (b.maxLat - b.minLat)) * H,
  });

  const line = route.map((k) => CITY_COORDS[k]).filter(Boolean).map(at);
  const hereKey = delivered ? destination : atCity;
  const here = hereKey && CITY_COORDS[hereKey] ? at(CITY_COORDS[hereKey]) : line[0];
  const dest = destination && CITY_COORDS[destination] ? at(CITY_COORDS[destination]) : null;
  const ink = tone === 'problem' ? 'var(--color-status-stuck)' : tone === 'done' ? 'var(--color-status-done)' : 'var(--color-info)';

  // Reference cities, minus any label that would sit on another one.
  const placed: Array<{ x: number; y: number }> = [];
  const refs = [...new Set([...REFERENCE_CITIES, ...route])].flatMap((k) => {
    const c = CITY_COORDS[k];
    if (!c) return [];
    const p = at(c);
    const clash = placed.some((q) => Math.abs(q.x - p.x) < 42 && Math.abs(q.y - p.y) < 12);
    if (!clash || k === destination) placed.push(p);
    return [{ k, ...p, label: !clash || k === destination }];
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto max-h-[300px]" role="img"
      aria-label={route.length ? `${nice(route[0])} → ${destination ? nice(destination) : '?'}` : ''}>
      <path d={`M${MOROCCO.map((c) => { const p = at(c); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; }).join('L')}Z`}
        fill="var(--color-bg-elevated)" stroke="var(--color-border-strong)" strokeWidth={0.8} strokeOpacity={0.6} strokeLinejoin="round" />
      {refs.map((r) => (
        <g key={r.k}>
          <circle cx={r.x} cy={r.y} r={2.2} fill="var(--color-text-tertiary)" />
          {r.label && (
            <text x={r.x > W - 62 ? r.x - 5 : r.x + 5} textAnchor={r.x > W - 62 ? 'end' : 'start'} y={r.y + 3.5} fontSize={10} fill={r.k === destination ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)'}
              fontWeight={r.k === destination ? 600 : 400}>{nice(r.k)}</text>
          )}
        </g>
      ))}
      {line.length > 1 && (
        <polyline points={line.map((p) => `${p.x},${p.y}`).join(' ')} fill="none" stroke={ink} strokeWidth={2}
          strokeLinecap="round" strokeLinejoin="round" strokeDasharray={moving ? '5 4' : undefined}
          className={moving ? 'dv-route-moving' : undefined} />
      )}
      {dest && <circle cx={dest.x} cy={dest.y} r={6} fill="none" stroke={ink} strokeWidth={1.6} />}
      {here && <circle cx={here.x} cy={here.y} r={4.2} fill={ink} />}
    </svg>
  );
}
